import {
  getDimensionsFromMap,
  ImageWMSLayer,
  useLayers,
  useOpenLayersContext,
} from '@ais-ui/react-openlayers';
import { useToast } from '@ais-ui/ui-core';
import { GetOverlayProps, useGetOverlayMP } from '@ais-ui/websockets';
import { useEffect, useRef, useState } from 'react';
import { useSettings } from '../Settings/SettingsProvider';
import { useAISMapContext } from './AISMapContext';
import { useFilterContext } from './FilterContext';
import { useLocalization } from '@ais-ui/locale-management';
import { useListenForReplay } from '../Replay/useListenForReplay';
import { useViewMode, ViewMode } from '../../components/ViewModeProvider';

const useGetOverlayWithMap = () => {
  const { map, displayProjection, dataProjection } = useOpenLayersContext();
  const [getOverlayProps, setGetOverlayProps] =
    useState<GetOverlayProps | null>(null);

  const { toast } = useToast();
  const { messages } = useLocalization();

  useEffect(() => {
    if (!map) return;

    const updateOverlayProps = () => {
      const dimensions = getDimensionsFromMap(
        map,
        displayProjection,
        dataProjection
      );
      if (dimensions) {
        const { center, zoomLevel, size, northEast, southWest } = dimensions;

        let centerLat = center.lat;
        let centerLon = center.lng;

        const isNullIsland = centerLat === 0 && centerLon === 0;
        if (isNullIsland) {
          // The backend crashes if we give it a center coordinate of (0, 0), instead we give it (0.0, 0.0)
          centerLat = 0.0;
          centerLon = 0.0;
          toast({
            title: messages.genericToastTitleError,
            description: messages.mapOverlayErrorAtCoordinateZero,
            variant: 'warning',
          });
        }

        setGetOverlayProps({
          centerLat: centerLat,
          centerLon: centerLon,
          zoomLevel: Math.round(zoomLevel),
          width: size.width,
          height: size.height,
          leftLon: southWest.lng,
          rightLon: northEast.lng,
        });
      } else {
        setGetOverlayProps(null);
      }
    };

    // Initial props
    updateOverlayProps();

    // Update when movement ends (includes zoom) or size changes
    map.on('moveend', updateOverlayProps);
    map.on('change:size', updateOverlayProps);

    return () => {
      map.un('moveend', updateOverlayProps);
      map.un('change:size', updateOverlayProps);
    };
  }, [map, displayProjection, dataProjection]);

  return useGetOverlayMP(getOverlayProps);
};

export function AISMapLayer() {
  const { settings } = useSettings();
  const AISMapContext = useAISMapContext();
  const { viewMode } = useViewMode();
  const filterContext = useFilterContext();

  const [replayOverlay, setReplayOverlay] = useListenForReplay();
  const [hasViewModeChanged, setHasViewModeChanged] = useState<boolean>(false);
  const prevViewMode = useRef(viewMode);
  const prevOverlayCreatedDatetime = useRef('');
  const { isCanvasLoaded, setIsCanvasLoaded } = useLayers();

  const { toast } = useToast();

  const { map } = useOpenLayersContext();

  const { data: overlayData, refetch } = useGetOverlayWithMap();

  useEffect(() => {
    if (
      overlayData &&
      overlayData.overlayCreatedDatetime &&
      overlayData.overlay &&
      viewMode !== ViewMode.Replay &&
      prevOverlayCreatedDatetime.current !== overlayData.overlayCreatedDatetime
    ) {
      prevOverlayCreatedDatetime.current = overlayData.overlayCreatedDatetime;
      const date = new Date(overlayData.overlayCreatedDatetime);

      AISMapContext.setOverlay(overlayData.overlay);
      AISMapContext.setCreatedAt(date);
      AISMapContext.setTargetCountInfo(overlayData.targetCountInfo);
      if (!isCanvasLoaded) {
        setIsCanvasLoaded(true);
      }
    }
  }, [overlayData, AISMapContext, isCanvasLoaded, setIsCanvasLoaded]);

  useEffect(() => {
    const prevMode = prevViewMode.current;
    const isReplayTransition =
      prevMode === ViewMode.Replay || viewMode === ViewMode.Replay;

    if (prevMode !== viewMode) {
      // Only recreate the layer when transitioning to/from Replay mode
      // Drawing mode should preserve the existing overlay
      if (isReplayTransition) {
        setHasViewModeChanged(true);
      }
      setReplayOverlay(null);
    }

    if (viewMode === ViewMode.Replay) {
      AISMapContext.setCreatedAt(undefined);
    }
    // Update prevViewMode to the current viewMode for the next render
    prevViewMode.current = viewMode;
  }, [viewMode, setReplayOverlay]);

  useEffect(() => {
    if (viewMode !== ViewMode.Replay && overlayData) {
      // Refetch when not in replay mode and have valid overlay data
      refetch();
    }
    // Do not add overlayData to dependencies as it retrigger too often
  }, [refetch, settings, AISMapContext.trigger, viewMode]);

  useEffect(() => {
    const applyAndRefresh = async () => {
      const success = await filterContext.applyFilter(true);
      if (success) {
        setTimeout(() => {
          AISMapContext.setTrigger((prevTrigger) => prevTrigger + 1);
        }, 100);
      }
    };
    applyAndRefresh();
  }, [filterContext, toast]);

  return (
    <ImageWMSLayer
      map={map}
      overlay={
        viewMode === ViewMode.Replay ? replayOverlay : AISMapContext.overlay
      }
      hasViewModeChanged={hasViewModeChanged}
      setHasViewModeChanged={setHasViewModeChanged}
      name="Vessels"
    />
  );
}
